import { Link } from "react-router-dom";
import { ArrowLeft, Shield, FileText, Cookie, Scale, Mail, ArrowRight } from "lucide-react";
import { LEGAL } from "@/lib/legal";

const SECTIONS = [
  {
    to: "/privacidad",
    icon: Shield,
    title: "Política de privacidad",
    desc: "Qué datos guardamos, para qué los usamos y tus derechos sobre ellos.",
    blob: "blob-mint",
    testid: "legal-link-privacidad",
  },
  {
    to: "/terminos",
    icon: FileText,
    title: "Términos y condiciones",
    desc: "Suscripción, prueba gratuita, renovación, cancelación y uso aceptable.",
    blob: "blob-lavender",
    testid: "legal-link-terminos",
  },
  {
    to: "/cookies",
    icon: Cookie,
    title: "Política de cookies",
    desc: "Solo usamos cookies estrictamente necesarias. Nada de tracking publicitario.",
    blob: "blob-peach",
    testid: "legal-link-cookies",
  },
  {
    to: "/aviso-legal",
    icon: Scale,
    title: "Aviso legal",
    desc: "Quién presta el servicio, propiedad intelectual y ley aplicable.",
    blob: "blob-rose",
    testid: "legal-link-aviso",
  },
];

export default function LegalIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-black/[0.05]">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-index-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display text-base leading-none">
              <span className="font-black">strato</span>
              <span className="font-normal text-foreground/55 ml-1 text-xs">by abstudio</span>
            </span>
          </Link>
          <span className="text-xs text-foreground/55">Vigente desde {LEGAL.effectiveDate}</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 relative">
        <div className="blob blob-cream w-[320px] h-[320px] -top-10 -right-10 -z-10" />
        <div className="blob blob-sky w-[280px] h-[280px] top-40 -left-16 -z-10" style={{ animationDelay: "2s" }} />

        <span className="tag-pill tag-pill-mint mb-5 relative"><Scale className="w-3 h-3" /> Información legal</span>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95] relative">
          Todo lo que necesitas <br />
          saber sobre <span className="scribble-underline scribble-mint">Strato</span>.
        </h1>
        <p className="mt-6 text-foreground/65 max-w-2xl leading-relaxed relative">
          Aquí están los documentos legales que regulan el uso de la plataforma. Están escritos en lenguaje claro,
          sin letra pequeña ni trucos. Si tienes cualquier duda, escríbenos a{" "}
          <a href={`mailto:${LEGAL.contactEmail}`} className="underline font-semibold text-foreground">{LEGAL.contactEmail}</a>.
        </p>

        <div className="grid md:grid-cols-2 gap-5 mt-12 relative">
          {SECTIONS.map(({ to, icon: Icon, title, desc, testid }) => (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              className="group app-card p-7 hover:border-foreground/30 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-2xl bg-foreground/[0.04] grid place-items-center shrink-0 group-hover:bg-foreground group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-xl font-black tracking-[-0.02em]">{title}</h2>
                  <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{desc}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold mt-3 text-foreground/55 group-hover:text-foreground transition-colors">
                    Leer documento <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 app-card p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative">
          <div>
            <h3 className="font-display text-lg font-black tracking-[-0.02em]">¿Algo no queda claro?</h3>
            <p className="text-sm text-foreground/65 mt-1">Te respondemos personalmente en menos de 48 horas.</p>
          </div>
          <a
            href={`mailto:${LEGAL.contactEmail}`}
            className="btn-apple-outline text-sm shrink-0"
            data-testid="legal-contact-email"
          >
            <Mail className="w-3.5 h-3.5" /> {LEGAL.contactEmail}
          </a>
        </div>

        <div className="mt-12 text-xs text-foreground/55 relative">
          <p>
            <strong className="text-foreground">{LEGAL.legalName}</strong> · {LEGAL.country} · Dominio {LEGAL.domain}
          </p>
        </div>
      </main>
    </div>
  );
}
